import React, { useEffect } from "react";
import { GetStaticPaths } from "next";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";

import styles from "src/styles/affiliate.scss";
import { renderRichText } from "src/services/PrismicService";
import { OfferItem, StateItem } from "src/services/OfferService";
import OfferCard from "src/components/OfferCard";
import { STATES, STATE_MAP } from "src/constants";
import { initializeApollo } from "src/lib/apollo-client";
import { prepareOfferStates } from "src/services/OfferService";
import { EXTRAS_CONTENT_QUERY, OFFERS_QUERY } from "src/gql/queries";

export type OffersTypes = "casino" | "sportsbook";
export const offersTypesArray: OffersTypes[] = ["casino", "sportsbook"];

export interface AffiliatePageProps {
  stateAbbrev: string;
  states: StateItem[];
  offerType: OffersTypes;
  extrasContent: string;
}

export interface PrismicExtrasReturnType {
  prismicExtras: { content: string };
}

const PAGE_TITLES = {
  casino: "Top Casino Promos | Sportsbook Casino in your State | BetQL",
  sportsbook: "Top Sports Promos | Sports Offers in your State | BetQL",
};

export const getOfferTypeFromString = (offerParam: string): OffersTypes => {
  return offersTypesArray.find((offer) => offerParam.includes(offer)) || "sportsbook";
};

/**
 * Generate local classname
 */

const cName = (name: string) => `rotoql-affiliate__${name}`;

/**
 * FAQ data
 */

const FAQ = [
  {
    question: "Can I sign up and create multiple accounts?",
    answer: `These sports offers are specifically for first time
    customers and first time deposits. If you already have a
    FanDuel or Draftkings daily fantasy account you will not
    be eligible to claim these offers.`,
  },
  {
    question: "Are there restrictions on betting with bonuses?",
    answer: `It depends which sports you are betting with if
    there are restrictions on how you can use your bonus
    funds. Currently most sports sites are using match
    bonuses and free bets which do not have restrictions.
    There are most likely roll over restrictions where you
    must bet the amount you were given before you can
    withdraw funds. You can check each sports site's
    restrictions when you sign up.`,
  },
  {
    question: "I am having issues verifying my location",
    answer: `If you are having trouble with the sports sites location
    verification check out our FAQ section. You will find
    info about location verification for all the major
    sports sites.`,
  },
  {
    question: "I am having issues depositing money",
    answer: `If you are having issues or running into errors
    depositing money to the sportsbooks check out our full
    FAQ section. Many users have issues depositing depending
    on their bank, as some banks are known to decline
    transactions with online wagering companies.`,
  },
];

/**
 * Page
 */

const AffiliateOffersPage = ({ states = [], stateAbbrev, offerType, extrasContent }: AffiliatePageProps) => {
  const router = useRouter();

  const stateIndex = states.findIndex((state) => state.abbreviation === stateAbbrev); // eslint-disable-line
  const offerState = states[stateIndex] ?? { [`${offerType}_offers`]: [] };

  useEffect(() => {
    if (!states?.length) {
      router.push("/500");
    }
    if (!offerState) {
      router.push("/404");
    }
  }, [states, offerState, router]);

  const stateTitle = stateIndex === 0 ? "" : offerState.name;
  const seoTitle = offerState.offers_title;
  const seoDesciption = offerState.offers_description;

  const pageTitle = PAGE_TITLES[offerType];
  const offersToUse = `${offerType}_offers` as const;

  return (
    <div className={cName("page-container")}>
      <NextSeo
        title={seoTitle ?? pageTitle}
        description={seoDesciption}
        openGraph={{
          url: `https://www.betql.co/${offerType}-offers`,
          title: pageTitle,
        }}
      />
      <div className={cName("hero")}>
        <h1 className={cName("title")}>
          {stateTitle ? `${stateTitle} ` : ""}
          {{ casino: "Casino", sportsbook: "Sports" }[offerType]} Sign Up Offers
        </h1>
      </div>

      {/* Offer list desktop */}

      <div className={cName("offer-list")}>
        {/* Table head */}
        <div className={cName("offer-list-head-row")}>
          <div className={cName("offer-list-head-cell")}>Betting Site</div>
          <div className={cName("offer-list-head-cell")}>Offer</div>
          <div className={cName("offer-list-head-cell")}>Rating &amp; Details</div>
          <div className={cName("offer-list-head-cell")} />
        </div>
        {/* Table body */}
        {offerState[offersToUse].map((offer: OfferItem, index: number) => (
          <OfferCard key={index} offer={offer} state={offerState} />
        ))}
      </div>

      {offerState.offers_content && (
        <div className={cName("seo-content")}>{renderRichText(offerState.offers_content)}</div>
      )}

      <div className={cName("faq-container")}>
        <h1 className={cName("faq-title")}>Frequently asked questions</h1>
        {FAQ.map(({ question, answer }, index) => (
          <div key={index} className={cName("faq-item")}>
            <h2 className={cName("faq-question")}>{question}</h2>
            <p className={cName("faq-answer")}>{answer}</p>
          </div>
        ))}
      </div>
      {extrasContent ? <div className="extras-content">{renderRichText(extrasContent)}</div> : null}
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

const affiliateQueries = (affiliateOfferUid: string) => {
  const client = initializeApollo();
  return Promise.all([
    client.query({
      query: OFFERS_QUERY,
    }),
    client.query<PrismicExtrasReturnType>({
      query: EXTRAS_CONTENT_QUERY,
      variables: {
        uid: affiliateOfferUid,
      },
    }),
  ]);
};

export const getAffiliateServerSideProps = async (state: string, offer: OffersTypes) => {
  const userLocale = STATE_MAP[state] ? state : "CA";
  const affiliate: string = `${offer}-offers-${userLocale.toLowerCase()}`;
  const [statesResponse, extrasContentResponse] = await affiliateQueries(affiliate);
  return {
    props: {
      extrasContent: extrasContentResponse?.data?.prismicExtras?.content || "",
      stateAbbrev: userLocale,
      states: prepareOfferStates(statesResponse).data,
      offerType: offer,
    },
  };
};

export const affiliateGetStaticPaths: GetStaticPaths = async () => {
  const paths = STATES.map((state) => ({
    params: {
      state: state.value.toLowerCase(),
    },
  })).flat();
  return {
    paths: paths,
    fallback: false,
  };
};

export const getAffiliateStaticProps = async (state: string, offer: OffersTypes) => {
  const affiliatefOffersUid: string = `${offer}-offers-${state}`;
  const stateAbbrev = state.toUpperCase();
  const [statesResponse, extrasContentResponse] = await affiliateQueries(affiliatefOffersUid);
  return {
    props: {
      extrasContent: extrasContentResponse?.data?.prismicExtras?.content || "",
      stateAbbrev: stateAbbrev,
      states: prepareOfferStates(statesResponse).data,
      offerType: offer,
    },
    revalidate: 60 * 60 * 3, // every 3 hours
  };
};

export default AffiliateOffersPage;
